import isEmpty from 'lodash/isEmpty';
import {
    CHECKOUT_FORM_NAME,
    PAYMENT_METHODS,
    PAYMENT_OPTIONS_METHODS_TYPES,
} from '../constants';
import { PaymentMethodType } from '../models/paymentMethods/enums';
import { PaymentMethod } from '../models/paymentMethods/paymentMethod';
import { PAYMENT_METHODS_METADATA } from '../models/paymentMethods/paymentMethodMetadata';
import { PaymentOptionInstrument } from '../models/paymentMethods/paymentOption';
import { CheckoutState } from '../state';
import { checkGooglePayFeatureGateIfReady } from '../utils/googlePay';
import { getSelectedVirtualIncentivesAggregatedTotal } from '../utils/virtualIncentives';
import {
    isApplePayInstanceCreated,
    isApplePaySortedAndPreselected,
} from './applePay';
import { getCanMakePaymentsWithGooglePay } from './googlePay';
import { getUnformattedOrderCost } from './order';
import {
    getConstrainedInstrumentType,
    getConstrainedPaymentMethods,
} from './tickets';
import { getNamespaceValue } from './utils/selector';
import { getSelectedVirtualIncentives } from './virtualIncentive';

const getSortedPaymentMethods = <
    SortedPaymentMethods extends Record<string, number>,
>(
    paymentMethods: SortedPaymentMethods,
    firstPaymentMethod: keyof SortedPaymentMethods,
) =>
    Object.entries(paymentMethods)
        .sort(([_lhsId, lhsOrder], [_rhsId, rhsOrder]) => lhsOrder - rhsOrder)
        .reduce<Array<keyof SortedPaymentMethods>>(
            (acc, [paymentMethod]) =>
                paymentMethod === firstPaymentMethod
                    ? [paymentMethod, ...acc]
                    : [...acc, paymentMethod],
            [],
        )
        .reduce<Record<keyof SortedPaymentMethods, number>>(
            (acc, paymentMethod, i) => ({ ...acc, [paymentMethod]: i }),
            {} as never,
        );

const INSTRUMENT_TYPE_RELATIVE_ORDER_BY_COUNTRY = {
    NL: {
        [PaymentMethodType.FPP_PAYPAL]: 0,
        [PaymentMethodType.IDEAL]: 1,
        [PaymentMethodType.CREDIT]: 2,
        [PaymentMethodType.NONCE_BRAINTREE]: 3,
        [PaymentMethodType.APPLE_PAY]: 4,
        [PaymentMethodType.GOOGLE_PAY]: 5,
        [PaymentMethodType.MAESTRO_BANCONTACT]: 6,
        [PaymentMethodType.SOFORT]: 7,
    },
    BE: {
        [PaymentMethodType.FPP_PAYPAL]: 0,
        [PaymentMethodType.MAESTRO_BANCONTACT]: 1,
        [PaymentMethodType.CREDIT]: 2,
        [PaymentMethodType.NONCE_BRAINTREE]: 3,
        [PaymentMethodType.APPLE_PAY]: 4,
        [PaymentMethodType.GOOGLE_PAY]: 5,
        [PaymentMethodType.IDEAL]: 6,
        [PaymentMethodType.SOFORT]: 7,
    },
    DE: {
        [PaymentMethodType.FPP_PAYPAL]: 0,
        [PaymentMethodType.CREDIT]: 1,
        [PaymentMethodType.NONCE_BRAINTREE]: 2,
        [PaymentMethodType.APPLE_PAY]: 3,
        [PaymentMethodType.GOOGLE_PAY]: 4,
        [PaymentMethodType.SEPA_DIRECT_DEBIT]: 5,
        [PaymentMethodType.SOFORT]: 6,
        [PaymentMethodType.IDEAL]: 7,
        [PaymentMethodType.MAESTRO_BANCONTACT]: 8,
    },
    BR: {
        [PaymentMethodType.FPP_PAYPAL]: 0,
        [PaymentMethodType.CREDIT]: 1,
        [PaymentMethodType.BOLETO_BANCARIO]: 2,
        [PaymentMethodType.APPLE_PAY]: 3,
        [PaymentMethodType.GOOGLE_PAY]: 4,
    },
    AR: {
        [PaymentMethodType.FPP_PAYPAL]: 0,
        [PaymentMethodType.CREDIT]: 1,
        [PaymentMethodType.BARCODE]: 2,
        [PaymentMethodType.APPLE_PAY]: 3,
        [PaymentMethodType.GOOGLE_PAY]: 4,
    },
    MX: {
        [PaymentMethodType.FPP_PAYPAL]: 0,
        [PaymentMethodType.CREDIT]: 1,
        [PaymentMethodType.BARCODE]: 2,
        [PaymentMethodType.APPLE_PAY]: 3,
        [PaymentMethodType.GOOGLE_PAY]: 4,
    },
};

const INSTRUMENT_TYPE_RELATIVE_ORDER_BY_COUNTRY_WITH_APPLE_PAY_FIRST =
    Object.entries(INSTRUMENT_TYPE_RELATIVE_ORDER_BY_COUNTRY).reduce(
        (acc, [countryCode, paymentMethods]) => ({
            ...acc,
            [countryCode]: getSortedPaymentMethods(
                paymentMethods,
                PaymentMethodType.APPLE_PAY,
            ),
        }),
        {} as typeof INSTRUMENT_TYPE_RELATIVE_ORDER_BY_COUNTRY,
    );

const DEFAULT_COUNTRY_INSTRUMENT_TYPE_RELATIVE_ORDER_WITH_APPLE_PAY_FIRST = {
    [PaymentMethodType.APPLE_PAY]: 0,
    [PaymentMethodType.GOOGLE_PAY]: 1,
    [PaymentMethodType.FPP_PAYPAL]: 2,
    [PaymentMethodType.CREDIT]: 3,
    [PaymentMethodType.NONCE_BRAINTREE]: 4,
    [PaymentMethodType.SOFORT]: 5,
    [PaymentMethodType.IDEAL]: 6,
    [PaymentMethodType.MAESTRO_BANCONTACT]: 7,
    [PaymentMethodType.SEPA_DIRECT_DEBIT]: 8,
};

const DEFAULT_COUNTRY_INSTRUMENT_TYPE_RELATIVE_ORDER = {
    [PaymentMethodType.FPP_PAYPAL]: 0,
    [PaymentMethodType.CREDIT]: 1,
    [PaymentMethodType.NONCE_BRAINTREE]: 2,
    [PaymentMethodType.APPLE_PAY]: 3,
    [PaymentMethodType.GOOGLE_PAY]: 4,
    [PaymentMethodType.SOFORT]: 5,
    [PaymentMethodType.IDEAL]: 6,
    [PaymentMethodType.MAESTRO_BANCONTACT]: 7,
    [PaymentMethodType.SEPA_DIRECT_DEBIT]: 8,
};

const UNKNOWN_INSTRUMENT_TYPE_RELATIVE_ORDER = Number.MAX_SAFE_INTEGER;

const getDefaultCountryInstrumentTypeRelativeOrder = (
    isApplePayFirst: boolean,
) =>
    isApplePayFirst
        ? DEFAULT_COUNTRY_INSTRUMENT_TYPE_RELATIVE_ORDER_WITH_APPLE_PAY_FIRST
        : DEFAULT_COUNTRY_INSTRUMENT_TYPE_RELATIVE_ORDER;

const getInstrumentTypeRelativeOrderForCountry = ({
    isApplePayFirst,
    country,
}: {
    isApplePayFirst: boolean;
    country?: string;
}) => {
    const instrumentTypesByCountry = isApplePayFirst
        ? INSTRUMENT_TYPE_RELATIVE_ORDER_BY_COUNTRY_WITH_APPLE_PAY_FIRST
        : INSTRUMENT_TYPE_RELATIVE_ORDER_BY_COUNTRY;

    return country
        ? instrumentTypesByCountry[
              country as keyof typeof instrumentTypesByCountry
          ]
        : null;
};

export const getAvailablePaymentMethods = (state: CheckoutState) => {
    const filterDisabledPaymentMethods = (
        paymentMethod: PaymentOptionInstrument,
    ) => {
        switch (paymentMethod.type) {
            case PaymentMethodType.NONCE_BRAINTREE:
                return state.app.featureFlags.enableNonceBraintreePayments;
            default:
                return true;
        }
    };

    const filterBasedOnPaymentConstraints = (
        paymentMethod: PaymentOptionInstrument,
    ) => {
        const constrainedInstrumentType = getConstrainedInstrumentType(state);
        const constrainedPaymentMethods = getConstrainedPaymentMethods(state);

        if (isEmpty(constrainedPaymentMethods)) {
            return true;
        }

        return (
            paymentMethod.type === constrainedInstrumentType ||
            paymentMethod.variants.some((variant) =>
                constrainedPaymentMethods.includes(variant),
            )
        );
    };

    const filterApplePay = (paymentMethod: PaymentOptionInstrument) =>
        paymentMethod.type === PaymentMethodType.APPLE_PAY
            ? isApplePayInstanceCreated(state)
            : true;

    const availablePaymentMethods = getNamespaceValue(
        state.order,
        'availablePaymentMethods',
        [],
    );

    const filterGooglePay = (paymentMethod: PaymentOptionInstrument) =>
        paymentMethod.type === PaymentMethodType.GOOGLE_PAY
            ? checkGooglePayFeatureGateIfReady(
                  getCanMakePaymentsWithGooglePay(state),
              )
            : true;

    if ((availablePaymentMethods?.length ?? 0) === 0) {
        return [];
    }

    const availableMethodTypes = [
        PAYMENT_OPTIONS_METHODS_TYPES.EVENTBRITE,
        PAYMENT_OPTIONS_METHODS_TYPES.OFFLINE,
        PAYMENT_OPTIONS_METHODS_TYPES.PAYPAL,
        PAYMENT_OPTIONS_METHODS_TYPES.AUTHNET,
    ];

    const venue = getNamespaceValue(state.event, 'venue', {
        address: undefined,
    });
    const { country } = venue.address || {};

    const isApplePayFirst = isApplePaySortedAndPreselected(state);
    const instrumentsForCountry = getInstrumentTypeRelativeOrderForCountry({
        isApplePayFirst,
        country,
    });

    const relativeOrderForInstrumentType =
        instrumentsForCountry ||
        getDefaultCountryInstrumentTypeRelativeOrder(isApplePayFirst);

    return availablePaymentMethods
        .filter((method) => availableMethodTypes.includes(method.type))
        .flatMap((method) => method.instrumentTypes)
        .filter((instrument) => filterDisabledPaymentMethods(instrument))
        .filter((instrument) => filterBasedOnPaymentConstraints(instrument))
        .filter((instrument) => filterApplePay(instrument))
        .filter((instrument) => filterGooglePay(instrument))
        .flatMap<PaymentMethod>((instrument) => {
            const currency = getNamespaceValue(state.event, 'currency');
            const isRapipagoPagofacil =
                instrument.type === PaymentMethodType.BARCODE &&
                currency === 'ARS';
            const pagofacilPaymentMethod = {
                type: PaymentMethodType.PAGOFACIL,
                additionalData: {
                    note: instrument.note,
                },
                variants: [],
            };
            const rapipagoPaymentMethod = {
                type: PaymentMethodType.RAPIPAGO,
                additionalData: {
                    note: instrument.note,
                },
                variants: [],
            };

            return isRapipagoPagofacil
                ? [pagofacilPaymentMethod, rapipagoPaymentMethod]
                : [
                      {
                          type: instrument.type,
                          additionalData: {
                              note: instrument.note,
                          },
                          variants: instrument.variants || [],
                      },
                  ];
        })
        .sort((lhs, rhs) => {
            const lhsRelativeOrder =
                relativeOrderForInstrumentType[
                    lhs.type as keyof typeof relativeOrderForInstrumentType
                ] ?? UNKNOWN_INSTRUMENT_TYPE_RELATIVE_ORDER;

            const rhsRelativeOrder =
                relativeOrderForInstrumentType[
                    rhs.type as keyof typeof relativeOrderForInstrumentType
                ] ?? UNKNOWN_INSTRUMENT_TYPE_RELATIVE_ORDER;

            return lhsRelativeOrder - rhsRelativeOrder;
        });
};

export const getIsPayingWithVirtualIncentives = (state: CheckoutState) =>
    state.form[CHECKOUT_FORM_NAME]?.values?.payment?.payWithVirtualIncentives ??
    false;

export const getIsPayingWithVirtualIncentivesSupportedBySomeAvailablePaymentMethod =
    (state: CheckoutState) =>
        getAvailablePaymentMethods(state).some(({ type }) => {
            const paymentMethodInfo =
                PAYMENT_METHODS_METADATA[
                    type as keyof typeof PAYMENT_METHODS_METADATA
                ] ?? null;
            return paymentMethodInfo?.allowsSplitPaymentsWithVirtualIncentives;
        });

export const getIsPayingWithVirtualIncentivesSupportedBySelectedPaymentMethod =
    (state: CheckoutState) => {
        const selectedPaymentMethodType = getSelectedPaymentMethod(state);

        if (selectedPaymentMethodType === PaymentMethodType.NOT_SELECTED) {
            return true;
        }

        const paymentMethodInfo =
            (selectedPaymentMethodType &&
                PAYMENT_METHODS_METADATA[
                    selectedPaymentMethodType as keyof typeof PAYMENT_METHODS_METADATA
                ]) ||
            null;

        return (
            paymentMethodInfo?.allowsSplitPaymentsWithVirtualIncentives ?? false
        );
    };

export const getIsTogglingVirtualIncentivesSupportedBySelectedPaymentMethod = (
    state: CheckoutState,
) => {
    const selectedPaymentMethodType = getSelectedPaymentMethod(state);

    if (selectedPaymentMethodType === PaymentMethodType.NOT_SELECTED) {
        return true;
    }

    const paymentMethodInfo =
        (selectedPaymentMethodType &&
            PAYMENT_METHODS_METADATA[
                selectedPaymentMethodType as keyof typeof PAYMENT_METHODS_METADATA
            ]) ||
        null;

    return (
        paymentMethodInfo?.allowsChangingVirtualIncentivesWhenSelected ?? false
    );
};

export const getPaypalEmail = (state: CheckoutState) =>
    state.form[CHECKOUT_FORM_NAME]?.values?.payment?.paypalEmail;

export const getSelectedPaymentMethod = (state: CheckoutState) =>
    state.form[CHECKOUT_FORM_NAME]?.values?.paymentMethod ??
    PaymentMethodType.NOT_SELECTED;

export const getPaymentHistory = (state: CheckoutState) => state.paymentHistory;

export const getPaymentStatus = (state: CheckoutState) =>
    state.paymentHistory.payment_status;

export const isPaymentMethodSelectorFormComplete = (state: CheckoutState) => {
    const selectedPaymentMethod = getSelectedPaymentMethod(state);
    const availablePaymentMethods = getAvailablePaymentMethods(state);
    const paymentMethodIsMissing =
        availablePaymentMethods.length > 1 &&
        selectedPaymentMethod === PaymentMethodType.NOT_SELECTED;

    const isPaypalSelected =
        selectedPaymentMethod === PaymentMethodType.NONCE_BRAINTREE;
    const paypalEmail = getPaypalEmail(state);
    const isPayPalEmailValid = !!paypalEmail;

    if (paymentMethodIsMissing) {
        return false;
    }

    if (isPaypalSelected) {
        return isPayPalEmailValid;
    }

    return true;
};

export const getIsLoadingPaymentMethods = (state: CheckoutState) =>
    !!state.applepay?.isLoadingApplePay;

export const getIsPaymentMethodInfoMissing = (state: CheckoutState) => {
    const selectedVirtualIncentives = getSelectedVirtualIncentives(state);
    const totalAmountPayedWithVirtualIncentivesInCents =
        getSelectedVirtualIncentivesAggregatedTotal(selectedVirtualIncentives);
    const totalOrderCostInCents = getUnformattedOrderCost(state);
    const availablePaymentMethods = getAvailablePaymentMethods(state);
    const selectedPaymentMethod = getSelectedPaymentMethod(state);

    const isPaymentMethodExplicitlySelected =
        selectedPaymentMethod !== PAYMENT_METHODS.NOT_SELECTED;
    const isPaymentMethodRequired =
        totalOrderCostInCents > totalAmountPayedWithVirtualIncentivesInCents;

    // If there is only one payment method then it's implicitly selected by the attendee when placing the order
    const isExplicitPaymentMethodSelectionRequired =
        availablePaymentMethods.length > 1;

    const isPaymentMethodMissing =
        isPaymentMethodRequired &&
        isExplicitPaymentMethodSelectionRequired &&
        !isPaymentMethodExplicitlySelected;

    if (isPaymentMethodMissing) {
        return true;
    }

    const isPaypalSelected =
        selectedPaymentMethod === PAYMENT_METHODS.NONCE_BRAINTREE;
    const paypalEmail = getPaypalEmail(state);

    return isPaypalSelected && !paypalEmail;
};
